import { isEmpty } from 'lodash';
import * as queryString from 'query-string';

import { isId } from './coordinate';

import { CoordinateMode, CoordinateModel } from '../models/coordinate.model';
import { GetProductListModel } from '../models/products.model';

export const getCriteriaPayload = (coordinate: CoordinateModel, search: string) => {
    const {
        format,
        commspace,
        pricerange,
        type,
        category,
        duration,
        attributes,
        distribution,
        region,
        restrictions,
    }: queryString.ParsedQuery = queryString.parse(search, {
        arrayFormat: 'comma',
    });

    const getArr = (value: string | number | Array<string | number>) => (Array.isArray(value) ? value : [value]);
    const coords = validateCoordinate(coordinate);

    let individualProducts =
        isId(String(coordinate.name)) && coordinate.individualproduct ? [coordinate.individualproduct] : [];

    return {
        criteria: {
            attribute: (attributes ? getArr(attributes as string[]) : []) as string[],
            coordinate: isId(String(coordinate.name)) ? [] : isEmpty(coords) ? [] : [coords as CoordinateModel],
            format: (format ? getArr(format as string[]) : []) as string[],
            distribution: (distribution ? getArr(distribution as string[]) : []) as string[],
            region: (region ? getArr(region as string[]) : []) as string[],
            individualproducts: individualProducts,
            commspace: (commspace ? getArr(commspace as string[]) : []) as string[],
            pricerange: pricerange ? getArr(pricerange as string[]) : [],
            type: (type ? getArr(type as string[]) : []) as string[],
            category: (category ? getArr(category as string[]) : []) as string[],
            restrictions: (restrictions ? getArr(restrictions as string[]) : []) as string[],
            duration: duration ? getArr(duration as string[]) : [],
        },
    };
};

export const getSelectPayloadWithFilters = (
    coordinate: CoordinateModel,
    search: string,
    data?: Partial<GetProductListModel>
): GetProductListModel => {
    const { name, individualproduct } = coordinate;

    return {
        bookingyear: new Date().getFullYear(),
        limit: 24,
        offset: 0,
        sort: [
            {
                column: !name && !individualproduct ? 'distance' : 'name',
                direction: 0,
            },
        ],
        group: [],
        ...getCriteriaPayload(coordinate, search),
        ...data,
    };
};

export const getProductFilterPayload = (payload: {
    bookingyear?: number;
    coordinate?: CoordinateModel;
    search?: string;
}) => {
    const { coordinate, search, bookingyear } = payload;
    const criteria =
        coordinate && search
            ? getCriteriaPayload(coordinate, search)
            : { criteria: { coordinate: coordinate ? [coordinate] : [] } };

    return {
        limit: 9000,
        offset: 0,
        types: [],
        sort: [],
        group: [],
        bookingyear: bookingyear,
        ...criteria,
    };
};

export const validateCoordinate = (coordinate: CoordinateModel) => {
    const { mode, name, radius, latitude, longitude, city } = coordinate;

    if (CoordinateMode.City === +mode && name) {
        return { ...coordinate, radius: radius ?? 1000, name: Array.isArray(name) ? name.join(' ') : name };
    }

    if (CoordinateMode.Radius === +mode && radius && latitude && longitude) {
        return { mode, radius, latitude, longitude };
    }

    if (CoordinateMode.Kanton === +mode && latitude && longitude && name) {
        let cantonName;
        if (name.toString().includes('Canton of ')) {
            cantonName = (name as string).split(' of ')[1];
        } else if (name.toString().includes('Kanton')) {
            cantonName = (name as string).split('Kanton ')[1];
        } else {
            cantonName = name;
        }
        return { mode, latitude, longitude, name: cantonName };
    }

    if (CoordinateMode.Region === +mode && latitude && longitude && name && city) {
        return { mode, city, latitude, longitude, name };
    }

    if (CoordinateMode.Zip === +mode && latitude && longitude && name && city) {
        return { mode, city, latitude, longitude, name: (name as string).split(' ')[0] };
    }

    if (CoordinateMode.Suburb === +mode && latitude && longitude && name && city) {
        let parsedName: string = '';

        if (Array.isArray(name)) {
            parsedName = name[0];
        } else {
            parsedName = (name as string).split(',')[0];
        }
        return { mode, city, latitude, longitude, name: parsedName };
    }

    return {};
};
